import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, AlertCircle, Info } from 'lucide-react';
import { SEO } from '../components/SEO';

export const GettingStartedPage = () => {
  return (
    <div className="min-h-screen bg-cream">
      <SEO 
        title="Débuter l'Apiculture Urbaine | Guide Complet pour Installer une Ruche - UrbanHive"
        description="Guide détaillé pour débuter l'apiculture sur balcon : prérequis essentiels, choix de la ruche, calendrier de l'apiculteur urbain et conseils d'installation. Tout pour réussir votre première ruche en ville."
        keywords="débuter apiculture urbaine, installer ruche balcon, guide apiculture débutant, première ruche ville, calendrier apiculteur, prérequis ruche urbaine"
        url="/getting-started"
        image="https://images.unsplash.com/photo-1758522965838-a509b49c259d?w=1200"
      />
      <Header />
      
      <main>
        {/* Hero Section with Background Image */}
        <section className="relative py-24 overflow-hidden">
          {/* Background Image with Overlay */}
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1758522965838-a509b49c259d" 
              alt="Apiculteur débutant"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50"></div>
          </div>
          
          {/* Content */}
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white mb-4">
                Débuter l'Apiculture Urbaine
              </h1>
              <p className="text-lg text-white/90">
                Guide complet pour installer votre première ruche sur votre balcon en toute sécurité.
              </p>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto space-y-12">
              
              {/* Prérequis */}
              <Card className="border-2 border-sage/20">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 rounded-full bg-sage/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-6 w-6 text-sage" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2">Prérequis Essentiels</h2>
                      <p className="text-gray-600">Avant de vous lancer, assurez-vous d'avoir :</p>
                    </div>
                  </div>
                  <ul className="space-y-3 ml-16">
                    <li className="flex items-start">
                      <span className="text-honey mr-2">•</span>
                      <span className="text-gray-700">Un balcon ou terrasse d'au moins 4m² exposé sud ou sud-ouest</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-honey mr-2">•</span>
                      <span className="text-gray-700">L'accord de votre propriétaire ou copropriété</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-honey mr-2">•</span>
                      <span className="text-gray-700">Un budget de 300-500€ pour l'équipement de départ</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-honey mr-2">•</span>
                      <span className="text-gray-700">Disponibilité de 10-15 heures par an pour l'entretien</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Choix de la ruche */}
              <Card className="border-2 border-honey/20">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-12 h-12 rounded-full bg-honey/20 flex items-center justify-center flex-shrink-0">
                      <Info className="h-6 w-6 text-honey" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2">Choisir sa Ruche</h2>
                      <p className="text-gray-600">Deux modèles principaux sont adaptés à l'apiculture urbaine :</p>
                    </div>
                  </div>
                  <div className="ml-16 space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Ruche Dadant</h3>
                      <p className="text-gray-600 mb-2">La plus courante en France. Volume de 54 litres, 10 cadres.</p>
                      <p className="text-sm text-sage font-medium">✓ Idéale pour les débutants • Production de 10-20kg/an</p>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Ruche Warré</h3>
                      <p className="text-gray-600 mb-2">Plus écologique, gestion par éléments empilables.</p>
                      <p className="text-sm text-sage font-medium">✓ Moins interventionniste • Production de 5-15kg/an</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Conseils importants */}
              <Card className="border-2 border-red-200 bg-red-50">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <AlertCircle className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-serif font-bold text-gray-900 mb-2">Points d'Attention</h2>
                      <ul className="space-y-2 text-gray-700">
                        <li>⚠️ Ne jamais installer une ruche sans avoir suivi une formation</li>
                        <li>⚠️ Déclarer obligatoirement votre ruche à la DDPP</li>
                        <li>⚠️ Respecter les distances légales (5m min des habitations)</li>
                        <li>⚠️ Prévoir une source d'eau à proximité (10m max)</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Calendrier - Timeline visuelle */}
              <Card className="border-2 border-sage/20">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-serif font-bold text-gray-900 mb-8 text-center">Calendrier de l'Apiculteur Urbain</h2>
                  
                  {/* Timeline circulaire par saisons */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Printemps - Mars-Avril */}
                    <div className="relative group">
                      <div className="bg-gradient-to-br from-green-100 to-green-50 rounded-xl p-6 border-2 border-green-200 hover:border-green-400 transition-all hover:shadow-xl">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 rounded-full bg-green-200 flex items-center justify-center">
                            <span className="text-2xl">🌱</span>
                          </div>
                          <div>
                            <div className="text-xs text-green-600 font-semibold uppercase">Printemps</div>
                            <div className="text-lg font-bold text-gray-900">Mars-Avril</div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          Installation de l'essaim, première visite de printemps
                        </p>
                        <div className="mt-3 text-xs text-green-600 font-medium">🐝 Démarrage de la saison</div>
                      </div>
                    </div>

                    {/* Fin Printemps - Mai-Juin */}
                    <div className="relative group">
                      <div className="bg-gradient-to-br from-yellow-100 to-yellow-50 rounded-xl p-6 border-2 border-yellow-200 hover:border-yellow-400 transition-all hover:shadow-xl">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 rounded-full bg-yellow-200 flex items-center justify-center">
                            <span className="text-2xl">🌸</span>
                          </div>
                          <div>
                            <div className="text-xs text-yellow-700 font-semibold uppercase">Floraison</div>
                            <div className="text-lg font-bold text-gray-900">Mai-Juin</div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          Surveillance de l'essaimage, ajout de hausses
                        </p>
                        <div className="mt-3 text-xs text-yellow-700 font-medium">📈 Période d'expansion</div>
                      </div>
                    </div>

                    {/* Été - Juillet-Août */}
                    <div className="relative group">
                      <div className="bg-gradient-to-br from-orange-100 to-orange-50 rounded-xl p-6 border-2 border-orange-200 hover:border-orange-400 transition-all hover:shadow-xl">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 rounded-full bg-orange-200 flex items-center justify-center">
                            <span className="text-2xl">🍯</span>
                          </div>
                          <div>
                            <div className="text-xs text-orange-700 font-semibold uppercase">Été</div>
                            <div className="text-lg font-bold text-gray-900">Juillet-Août</div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          Récolte du miel, extraction
                        </p>
                        <div className="mt-3 text-xs text-orange-700 font-medium">✨ Récolte principale</div>
                      </div>
                    </div>

                    {/* Automne - Septembre */}
                    <div className="relative group">
                      <div className="bg-gradient-to-br from-red-100 to-red-50 rounded-xl p-6 border-2 border-red-200 hover:border-red-400 transition-all hover:shadow-xl">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 rounded-full bg-red-200 flex items-center justify-center">
                            <span className="text-2xl">🍂</span>
                          </div>
                          <div>
                            <div className="text-xs text-red-700 font-semibold uppercase">Automne</div>
                            <div className="text-lg font-bold text-gray-900">Septembre</div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          Traitement anti-varroa, nourrissement si nécessaire
                        </p>
                        <div className="mt-3 text-xs text-red-700 font-medium">💊 Préparation hivernale</div>
                      </div>
                    </div>

                    {/* Hiver - Octobre-Février */}
                    <div className="relative group md:col-span-2 lg:col-span-2">
                      <div className="bg-gradient-to-br from-blue-100 to-blue-50 rounded-xl p-6 border-2 border-blue-200 hover:border-blue-400 transition-all hover:shadow-xl">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-12 h-12 rounded-full bg-blue-200 flex items-center justify-center">
                            <span className="text-2xl">❄️</span>
                          </div>
                          <div>
                            <div className="text-xs text-blue-700 font-semibold uppercase">Hiver</div>
                            <div className="text-lg font-bold text-gray-900">Octobre-Février</div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          Hivernage, surveillance à distance. Les abeilles forment une grappe pour maintenir la chaleur et consomment leurs réserves de miel.
                        </p>
                        <div className="mt-3 text-xs text-blue-700 font-medium">🛌 Repos et surveillance</div>
                      </div>
                    </div>
                  </div>

                  {/* Légende */}
                  <div className="mt-8 p-4 bg-cream rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-600 text-center">
                      <strong>💡 Conseil :</strong> Ce calendrier est indicatif et peut varier selon votre région et les conditions climatiques. 
                      Adaptez vos interventions en fonction de l'observation de vos colonies.
                    </p>
                  </div>
                </CardContent>
              </Card>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};